import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, Eye, Clock } from "lucide-react";
import { API, COVER_IMAGE } from "../lib/constants";
import { useUser } from "../contexts/UserContext";
import WitnessStandTabs from "../components/WitnessStandTabs";

export default function WitnessStandStory() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("user_session_token");
    fetch(`${API}/witness-stand/stories/${storyId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setStory(d || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, [storyId]);

  const toggleLike = async () => {
    if (!user) return;
    const token = localStorage.getItem("user_session_token");
    const r = await fetch(`${API}/witness-stand/stories/${storyId}/like`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (!r.ok) return;
    const d = await r.json();
    if (d.liked !== undefined) {
      setLiked(d.liked);
      setStory(s => ({ ...s, likes_count: (s.likes_count || 0) + (d.liked ? 1 : -1) }));
    }
  };

  if (loading) return <p className="pt-32 text-center text-gray-500 font-mono text-xs">LOADING…</p>;
  if (!story || !story.id) return (
    <div className="pt-32 text-center px-6">
      <p className="text-gray-400 mb-4">Story not found.</p>
      <button onClick={() => navigate("/witness-stand")} className="px-4 py-2 bg-red-600 rounded-lg text-white font-bold text-sm">Back to Library</button>
    </div>
  );

  const minutes = Math.max(1, Math.round((story.word_count || 0) / 200));

  return (
    <div className="min-h-screen pb-20" data-testid="witness-stand-reader">
      {/* Hero */}
      <div className="relative h-64 md:h-96">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${story.cover_url || COVER_IMAGE})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="absolute top-4 left-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 px-3 py-1.5 rounded bg-black/60 backdrop-blur-sm text-xs text-white hover:bg-black/80" data-testid="reader-back">
            <ArrowLeft size={12}/>Back
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="max-w-3xl mx-auto">
            <p className="font-mono text-[11px] text-red-400 tracking-[0.3em] mb-2">
              {story.genre?.toUpperCase()} · BY {story.author_name?.toUpperCase()}
            </p>
            <h1 className="font-cinzel text-3xl md:text-5xl font-bold mb-3 leading-tight" data-testid="reader-title">{story.title}</h1>
            {story.subtitle && <p className="text-gray-300 text-base md:text-lg italic">{story.subtitle}</p>}
            <div className="flex items-center gap-4 mt-4 text-[11px] font-mono text-gray-400">
              <span className="flex items-center gap-1"><Clock size={11}/>{minutes} MIN READ</span>
              <span className="flex items-center gap-1"><Eye size={11}/>{story.view_count || 0}</span>
              <button onClick={toggleLike} className={`flex items-center gap-1 transition-colors ${liked ? "text-red-400" : "hover:text-red-400"}`} data-testid="reader-like">
                <Heart size={11} fill={liked ? "currentColor" : "none"}/>{story.likes_count || 0}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 mt-10 prose prose-invert prose-red font-serif" data-testid="reader-content">
        <WitnessStandTabs showBlurb={false} />
        {(story.content || "").split(/\n{2,}/).map((para, i) => (
          <p key={i} className="text-gray-200 leading-loose text-lg mb-5 whitespace-pre-wrap">{para}</p>
        ))}
      </article>
    </div>
  );
}
